import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { CheckCircle, ArrowRight, Star, AlertCircle } from 'lucide-react';

/**
 * Payment Success Page
  * Shown after successful Stripe or Maxio payment
   * Redirects authenticated users to the app, or prompts to sign up
    */
const PaymentSuccess = () => {
    const navigate = useNavigate();
    const { isAuthenticated, loginWithRedirect, isLoading } = useAuth0();
    const [searchParams] = useSearchParams();
    const [checkoutEmail, setCheckoutEmail] = useState(null);
    const [paymentVerified, setPaymentVerified] = useState(false);

    // Get email from URL parameters (passed by Maxio or Stripe)
    useEffect(() => {
          // Check for Stripe session_id first
          const sessionId = searchParams.get('session_id');

          // Also check for Maxio parameters
          const email = searchParams.get('email');
          const reference = searchParams.get('reference'); // Maxio subscription reference

          if (sessionId) {
                  // Stripe Payment Link redirect
                  console.log('Stripe session ID:', sessionId);
                  setPaymentVerified(true);
                  localStorage.setItem('stripe_session_id', sessionId);
                  localStorage.setItem('checkout_timestamp', new Date().toISOString());

                  // Note: Email from Stripe checkout will be retrieved via webhook
                  // The customer's email is stored in Supabase when the webhook fires
          } else if (email && email !== '{CUSTOMER_EMAIL}') {
                  // Maxio redirect with email parameter
                  setCheckoutEmail(email);
                  setPaymentVerified(true);
                  console.log('Checkout email from Maxio:', email);
                  console.log('Maxio reference:', reference);

                  // Save to localStorage for signup tracking
                  localStorage.setItem('checkout_email', email);
                  if (reference) {
                            localStorage.setItem('maxio_reference', reference);
                  }
                  localStorage.setItem('checkout_timestamp', new Date().toISOString());
          } else {
                  // No payment parameters found, but still show success page
                  // This handles edge cases where redirect happens without params
                  setPaymentVerified(true);
          }
    }, [searchParams]);

    useEffect(() => {
          if (isLoading) return; // Wait for auth state to load

          if (isAuthenticated) {
                  // If user is authenticated, redirect to app after 3 seconds
                  const timer = setTimeout(() => {
                            navigate('/app');
                  }, 3000);
                  return () => clearTimeout(timer);
          }
          // Removed auto-redirect for non-authenticated users - they must click the button
    }, [isAuthenticated, isLoading, navigate]);

    const handleContinue = () => {
          if (isAuthenticated) {
                  navigate('/app');
          } else {
                  const authOptions = {
                            appState: { returnTo: '/app' },
                            screen_hint: 'signup' // Suggest signup screen instead of login
                  };

                  // If we have the checkout email, pre-fill it in Auth0
                  if (checkoutEmail) {
                            authOptions.login_hint = checkoutEmail; // Pre-fills the email field in Auth0
                  }

                  loginWithRedirect(authOptions);
          }
    };

    return (
          <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-red-50 flex items-center justify-center p-4">
            <div className="max-w-2xl w-full">
              <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 border-4 border-green-500">
    {/* Success Icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-16 h-16 text-white" />
      </div>
      </div>

  {/* Success Message */}
            <h1 className="text-4xl md:text-5xl font-black text-center mb-4 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              Payment Successful!
  </h1>

          <p className="text-xl text-gray-700 text-center mb-8 font-semibold">
              Your subscription is now active. Welcome to StartPatriot!
  </p>

{/* Email Warning - Only show for non-authenticated users with checkout email */}
{!isAuthenticated && checkoutEmail && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-4 border-yellow-400 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-8 h-8 text-yellow-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-black text-gray-900 mb-2">
                      IMPORTANT: Use This Email to Sign Up
  </h3>
                   <p className="text-gray-800 font-bold mb-2">
                      You must sign up with this email address:
</p>
                   <p className="text-2xl font-black text-blue-600 mb-3 break-all">
{checkoutEmail}
  </p>
                   <p className="text-gray-700 font-medium">
                      This is the email you used at checkout. Using a different email will prevent you from accessing your paid subscription.
  </p>
  </div>
  </div>
  </div>
           )}

{/* Info for Stripe users - they need to use same email */}
{!isAuthenticated && !checkoutEmail && paymentVerified && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-4 border-blue-400 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-black text-gray-900 mb-2">
                      Sign Up with Your Payment Email
  </h3>
                   <p className="text-gray-700 font-medium">
                      Please sign up or log in using the same email address you used during checkout to access your subscription.
  </p>
  </div>
  </div>
  </div>
           )}

{/* What's Next */}
          <div className="bg-gradient-to-br from-blue-50 to-red-50 rounded-xl p-6 mb-8 border-2 border-blue-200">
                        <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-2">
                          <Star className="w-6 h-6 text-blue-600 fill-blue-600" />
                          What's Next?
            </h2>
            <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            <span className="text-gray-700 font-medium">
          {isAuthenticated
                               ? 'You have full access to all ASVAB prep materials'
                                : 'Sign up or log in to access your account'}
</span>
  </li>
              <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700 font-medium">
                    Take full-length practice tests to gauge your readiness
  </span>
  </li>
              <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700 font-medium">
                    Practice with 5,000+ questions across all 10 ASVAB subtests
  </span>
  </li>
              <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700 font-medium">
                    Track your AFQT score and monitor your progress
  </span>
  </li>
  </ul>
  </div>

{/* CTA Button */}
          <button
            onClick={handleContinue}
            className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-red-600 text-white text-xl font-black rounded-xl hover:from-blue-700 hover:to-red-700 transition shadow-lg flex items-center justify-center gap-3 border-2 border-blue-800"
          >
            {isAuthenticated ? 'Continue to App' : 'Sign Up to Access'}
            <ArrowRight className="w-6 h-6" />
              </button>

{!isLoading && isAuthenticated && (
              <p className="text-center text-gray-500 mt-4 text-sm font-medium">
                Redirecting to your account in a few seconds...
  </p>
           )}
</div>

{/* Support */}
        <div className="text-center mt-8">
                    <p className="text-gray-600 font-medium">
                      Questions? Contact us at{' '}
            <a href="mailto:support@startpatriot.com" className="text-blue-600 hover:underline font-bold">
                            support@startpatriot.com
              </a>
              </p>
              </div>
              </div>
              </div>
  );
};

export default PaymentSuccess;
